/**
 * Frame quality gate (Build 13a).
 *
 * The b12.3 live test captured 0 cells on 64% of frames — wasted CPU and
 * battery spent running Tesseract on frames that were too blurry (camera
 * mid-scroll) or too low-confidence to ever yield a usable read. This gate
 * runs BEFORE the expensive OCR pass and rejects a frame outright when
 * either:
 *   - its motion-blur metric is too high (the frame is smeared), OR
 *   - its per-frame confidence is below threshold.
 *
 * Target: drop the 0-cell frame rate from ~64% to ~10-15%.
 *
 * The gate itself is a pure decision function. A frame's "blur" is
 * expressed as a sharpness score in [0,1] (1 = crisp, 0 = fully smeared);
 * the helper [[sharpnessFromRgba]] derives that score from a luma-gradient
 * energy measure so the gate can be exercised on synthetic buffers in the
 * unit tests without a camera.
 */
import { loadFrameImage } from "./frameImageCache";
import { CANVAS_READBACK } from "./canvasReadback";

export interface FrameQualityThresholds {
  /**
   * Minimum sharpness (0..1) a frame must have to be worth OCRing. Frames
   * below this are treated as motion-blurred and skipped. Lower = more
   * permissive.
   */
  minSharpness: number;
  /**
   * Minimum per-frame confidence (0..1) a frame must carry. This is the
   * upstream detector's own confidence in the frame being a usable Pokédex
   * read; below it we skip rather than waste an OCR pass.
   */
  minConfidence: number;
}

export const DEFAULT_FRAME_QUALITY_THRESHOLDS: FrameQualityThresholds = {
  minSharpness: 0.35,
  minConfidence: 0.5,
};

export type FrameQualityReject = "motion_blur" | "low_confidence";

export interface FrameQualityDecision {
  /** True when the frame is good enough to spend an OCR pass on. */
  pass: boolean;
  /** Why the frame was rejected, when `pass` is false. */
  reason?: FrameQualityReject;
  /** The sharpness score considered (echoed for diagnostics). */
  sharpness: number;
  /** The confidence considered (echoed for diagnostics). */
  confidence: number;
}

export interface FrameQualityInput {
  /** Sharpness score 0..1 (1 = crisp). See [[sharpnessFromRgba]]. */
  sharpness: number;
  /** Per-frame confidence 0..1. */
  confidence: number;
}

/**
 * Decide whether a frame clears the quality bar. Blur is checked first so a
 * smeared frame is reported as "motion_blur" even if its confidence also
 * happens to be low — the actionable cause is the blur. Pure and total.
 */
export function evaluateFrameQuality(
  input: FrameQualityInput,
  thresholds: FrameQualityThresholds = DEFAULT_FRAME_QUALITY_THRESHOLDS,
): FrameQualityDecision {
  const sharpness = clamp01(input.sharpness);
  const confidence = clamp01(input.confidence);
  if (sharpness < thresholds.minSharpness) {
    return { pass: false, reason: "motion_blur", sharpness, confidence };
  }
  if (confidence < thresholds.minConfidence) {
    return { pass: false, reason: "low_confidence", sharpness, confidence };
  }
  return { pass: true, sharpness, confidence };
}

/**
 * Estimate a frame's sharpness in [0,1] from its RGBA buffer using mean
 * absolute luma gradient (a cheap focus measure). A crisp frame has strong
 * edges → high gradient energy → high sharpness; a motion-blurred frame
 * smears edges → low gradient energy → low sharpness.
 *
 * The raw gradient energy is normalised by `fullScaleGradient` (the mean
 * gradient a typically-sharp Pokédex grid produces) and clamped to [0,1],
 * so the returned score is directly comparable against
 * `FrameQualityThresholds.minSharpness`.
 */
export function sharpnessFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  fullScaleGradient = 40,
): number {
  if (width < 2 || height < 2) return 0;
  let total = 0;
  let count = 0;
  // Horizontal neighbour luma difference, row-major. One pass, no allocation.
  for (let y = 0; y < height; y++) {
    for (let x = 1; x < width; x++) {
      const i = (y * width + x) * 4;
      const j = (y * width + (x - 1)) * 4;
      const l1 = luma(data[i], data[i + 1], data[i + 2]);
      const l0 = luma(data[j], data[j + 1], data[j + 2]);
      total += Math.abs(l1 - l0);
      count += 1;
    }
  }
  if (count === 0) return 0;
  const meanGradient = total / count;
  return clamp01(meanGradient / fullScaleGradient);
}

/**
 * Decode a frame data URL to a small RGBA buffer and return its sharpness
 * in [0,1] via [[sharpnessFromRgba]]. Used by the live hook to run the blur
 * half of the gate BEFORE the expensive OCR pass.
 *
 * The frame is downsampled to at most `sampleEdge` px on its long side: a
 * motion-blur estimate does not need full resolution, and decoding ~160px
 * is orders of magnitude cheaper than a Tesseract pass on the full frame —
 * which is the whole point of gating here. Returns 1 (treat as sharp, i.e.
 * never blur-gate) if the frame can't be decoded in this environment, so a
 * missing canvas can never silently swallow every frame.
 */
export async function sharpnessFromDataUrl(
  dataUrl: string,
  sampleEdge = 160,
): Promise<number> {
  if (!dataUrl) return 1;
  if (typeof document === "undefined") return 1;
  let img: HTMLImageElement;
  try {
    img = await loadImage(dataUrl);
  } catch {
    return 1;
  }
  const longest = Math.max(img.width, img.height);
  if (longest < 2) return 0;
  const scale = longest > sampleEdge ? sampleEdge / longest : 1;
  const w = Math.max(2, Math.round(img.width * scale));
  const h = Math.max(2, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return 1;
  ctx.drawImage(img, 0, 0, w, h);
  let pixels: ImageData;
  try {
    pixels = ctx.getImageData(0, 0, w, h);
  } catch {
    return 1;
  }
  return sharpnessFromRgba(pixels.data, w, h);
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  // One decode per frame, shared by every consumer (lib/frameImageCache).
  return loadFrameImage(dataUrl);
}

function luma(r: number, g: number, b: number): number {
  // Rec. 709 luma — matches the native pipeline's pill detector.
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(1, v));
}
